// src/shared/services/analyticsService.js
export const analyticsService = {
    // Phân tích thói quen vay nợ
    analyzeDebtHabits(debts, payments) {
        const analysis = {
            averageDebtAmount: 0,
            mostCommonLender: '',
            averagePaymentTime: 0,
            debtFrequency: 0,
            commonPurposes: [],
            riskLevel: 'low'
        };

        if (debts.length === 0) return analysis;

        // Số tiền vay trung bình
        analysis.averageDebtAmount = debts.reduce((sum, debt) => sum + debt.soTien, 0) / debts.length;

        // Người cho vay thường xuyên nhất
        const lenderCount = {};
        debts.forEach(debt => {
            lenderCount[debt.nguoiVay] = (lenderCount[debt.nguoiVay] || 0) + 1;
        });
        analysis.mostCommonLender = Object.keys(lenderCount).reduce((a, b) =>
            lenderCount[a] > lenderCount[b] ? a : b, ''
        );

        // Thời gian trả nợ trung bình
        const paidDebts = debts.filter(debt => debt.daTra);
        if (paidDebts.length > 0) {
            const totalDays = paidDebts.reduce((sum, debt) => {
                const borrowDate = new Date(debt.ngayVay);
                const dueDate = new Date(debt.ngayTra);
                return sum + Math.ceil((dueDate - borrowDate) / (1000 * 60 * 60 * 24));
            }, 0);
            analysis.averagePaymentTime = Math.round(totalDays / paidDebts.length);
        }

        // Tần suất vay nợ (số lần/tháng)
        const monthsSpan = this.getMonthsSpan(debts);
        analysis.debtFrequency = monthsSpan > 0 ? (debts.length / monthsSpan).toFixed(1) : 0;

        // Mục đích vay thường gặp
        const purposeCount = {};
        debts.forEach(debt => {
            const purpose = debt.mucDich.toLowerCase();
            purposeCount[purpose] = (purposeCount[purpose] || 0) + 1;
        });
        analysis.commonPurposes = Object.entries(purposeCount)
            .sort(([, a], [, b]) => b - a)
            .slice(0, 3)
            .map(([purpose, count]) => ({ purpose, count }));

        // Đánh giá mức độ rủi ro
        analysis.riskLevel = this.calculateRiskLevel(debts, payments);

        return analysis;
    },

    // Dự đoán khả năng trả nợ
    predictPaymentCapability(debts, payments) {
        const prediction = {
            score: 0,
            level: 'good',
            factors: [],
            recommendations: []
        };

        let score = 100;
        const factors = [];

        // Tỷ lệ trả đúng hạn
        const onTimePayments = debts.filter(debt => {
            if (!debt.daTra) return false;
            const dueDate = new Date(debt.ngayTra);
            const lastPayment = payments
                .filter(p => p.debtId === debt.id)
                .sort((a, b) => new Date(b.ngayTra) - new Date(a.ngayTra))[0];
            return lastPayment && new Date(lastPayment.ngayTra) <= dueDate;
        }).length;

        const onTimeRate = debts.length > 0 ? (onTimePayments / debts.length) * 100 : 100;
        if (onTimeRate < 80) {
            score -= (80 - onTimeRate) * 0.5;
            factors.push(`Tỷ lệ trả đúng hạn: ${onTimeRate.toFixed(1)}%`);
        }

        // Số nợ quá hạn hiện tại
        const overdueDebts = debts.filter(debt =>
            !debt.daTra && new Date(debt.ngayTra) < new Date()
        ).length;
        if (overdueDebts > 0) {
            score -= overdueDebts * 10;
            factors.push(`${overdueDebts} khoản nợ quá hạn`);
        }

        // Tổng nợ so với khả năng trả
        const totalDebt = debts.reduce((sum, debt) => sum + debt.soTien, 0);
        const totalPaid = debts.reduce((sum, debt) => sum + (debt.soTienDaTra || 0), 0);
        const debtRatio = totalDebt > 0 ? ((totalDebt - totalPaid) / totalDebt) * 100 : 0;

        if (debtRatio > 70) {
            score -= (debtRatio - 70) * 0.3;
            factors.push(`Tỷ lệ nợ cao: ${debtRatio.toFixed(1)}%`);
        }

        prediction.score = Math.max(0, Math.min(100, score));
        prediction.factors = factors;

        // Xác định level
        if (prediction.score >= 80) prediction.level = 'excellent';
        else if (prediction.score >= 60) prediction.level = 'good';
        else if (prediction.score >= 40) prediction.level = 'fair';
        else prediction.level = 'poor';

        // Đưa ra khuyến nghị
        prediction.recommendations = this.generateRecommendations(prediction.score, factors);

        return prediction;
    },

    // Gợi ý kế hoạch trả nợ tối ưu
    generateOptimalPaymentPlan(debts) {
        const unpaidDebts = debts.filter(debt => !debt.daTra);
        if (unpaidDebts.length === 0) return [];

        // Sắp xếp theo độ ưu tiên: quá hạn > gần đến hạn > số tiền cao
        const prioritizedDebts = unpaidDebts.map(debt => {
            const remaining = debt.soTien - (debt.soTienDaTra || 0);
            const daysToDeadline = Math.ceil((new Date(debt.ngayTra) - new Date()) / (1000 * 60 * 60 * 24));

            let priority = 0;
            if (daysToDeadline < 0) priority = 100; // Quá hạn
            else if (daysToDeadline <= 7) priority = 80; // Gần đến hạn
            else if (daysToDeadline <= 30) priority = 60;
            else priority = 40;

            // Điều chỉnh theo số tiền
            priority += Math.min(remaining / 1000000, 20); // Tối đa +20 điểm

            return {
                ...debt,
                remaining,
                daysToDeadline,
                priority,
                suggestedPayment: this.calculateSuggestedPayment(debt, daysToDeadline)
            };
        }).sort((a, b) => b.priority - a.priority);

        return prioritizedDebts.map((debt, index) => ({
            rank: index + 1,
            debt,
            strategy: this.getPaymentStrategy(debt),
            urgency: this.getUrgencyLevel(debt.daysToDeadline)
        }));
    },

    // Helper methods
    getMonthsSpan(debts) {
        if (debts.length === 0) return 0;
        const dates = debts.map(debt => new Date(debt.ngayVay));
        const earliest = new Date(Math.min(...dates));
        const latest = new Date(Math.max(...dates));
        return Math.ceil((latest - earliest) / (1000 * 60 * 60 * 24 * 30));
    },

    calculateRiskLevel(debts, payments) {
        const overdueCount = debts.filter(debt =>
            !debt.daTra && new Date(debt.ngayTra) < new Date()
        ).length;

        const totalDebt = debts.reduce((sum, debt) => sum + debt.soTien, 0);
        const totalPaid = debts.reduce((sum, debt) => sum + (debt.soTienDaTra || 0), 0);
        const debtRatio = totalDebt > 0 ? ((totalDebt - totalPaid) / totalDebt) * 100 : 0;

        if (overdueCount > 2 || debtRatio > 80) return 'high';
        if (overdueCount > 0 || debtRatio > 50) return 'medium';
        return 'low';
    },

    generateRecommendations(score, factors) {
        const recommendations = [];

        if (score < 60) {
            recommendations.push('Ưu tiên trả các khoản nợ quá hạn');
            recommendations.push('Lập kế hoạch trả nợ chi tiết');
        }

        if (factors.some(f => f.includes('quá hạn'))) {
            recommendations.push('Liên hệ với người cho vay để thương lượng');
        }

        if (factors.some(f => f.includes('Tỷ lệ nợ cao'))) {
            recommendations.push('Hạn chế vay nợ mới');
            recommendations.push('Tăng cường thanh toán');
        }

        return recommendations;
    },

    calculateSuggestedPayment(debt, daysToDeadline) {
        const remaining = debt.soTien - (debt.soTienDaTra || 0);

        if (daysToDeadline < 0) return remaining; // Quá hạn - trả hết
        if (daysToDeadline <= 7) return Math.max(remaining * 0.5, 100000); // Gần hạn - trả 50%
        if (daysToDeadline <= 30) return Math.max(remaining * 0.3, 50000); // Trả 30%
        return Math.max(remaining * 0.2, 50000); // Trả 20%
    },

    getPaymentStrategy(debt) {
        if (debt.daysToDeadline < 0) return 'Khẩn cấp - Trả ngay';
        if (debt.daysToDeadline <= 7) return 'Ưu tiên cao - Trả trong tuần';
        if (debt.daysToDeadline <= 30) return 'Ưu tiên trung bình - Trả trong tháng';
        return 'Ưu tiên thấp - Trả dần';
    },

    getUrgencyLevel(daysToDeadline) {
        if (daysToDeadline < 0) return 'critical';
        if (daysToDeadline <= 7) return 'high';
        if (daysToDeadline <= 30) return 'medium';
        return 'low';
    }
};
